/**
 * The audible "sweep due" prompt: a short two-tone beep through Web Audio, once per episode. An
 * episode is the time a given last complete sweep stays the last one while a sweep is due; a new
 * sweep ends it. Browsers only allow sound after a user gesture, hence the "enable sound" toggle.
 */

export type SweepState = "ok" | "due" | "overdue" | "unknown";

export function sweepState(
  ageS: number | null | undefined,
  timing: { expected_interval_h: number; overdue_after_h: number },
): SweepState {
  if (ageS === null || ageS === undefined) return "unknown";
  if (ageS > timing.overdue_after_h * 3600) return "overdue";
  if (ageS > timing.expected_interval_h * 3600) return "due";
  return "ok";
}

/** Decides when to sound: once for each episode key (the last complete sweep's time). */
export class PromptGate {
  private prompted = new Set<string>();

  shouldPrompt(state: SweepState, episode: string | null | undefined): boolean {
    if ((state !== "due" && state !== "overdue") || !episode) return false;
    if (this.prompted.has(episode)) return false;
    this.prompted.add(episode);
    return true;
  }
}

let ctx: AudioContext | null = null;

export function playPrompt(): void {
  try {
    const AC = window.AudioContext;
    if (AC === undefined) return;
    ctx ??= new AC();
    const t0 = ctx.currentTime;
    for (const [i, freq] of [880, 660].entries()) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t0 + i * 0.25);
      gain.gain.exponentialRampToValueAtTime(0.3, t0 + i * 0.25 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + i * 0.25 + 0.22);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0 + i * 0.25);
      osc.stop(t0 + i * 0.25 + 0.23);
    }
  } catch {
    // no audio device or blocked: the banner still shows
  }
}

const SOUND_KEY = "stockvol.sound";

export function soundEnabled(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) === "on";
  } catch {
    return false;
  }
}

export function setSoundEnabled(on: boolean): void {
  try {
    localStorage.setItem(SOUND_KEY, on ? "on" : "off");
  } catch {
    // private mode: the setting lasts for this page only
  }
}
